import React from 'react';
import { Broadcast, VideoCamera, User, Globe, CaretUp, CaretDown, Record } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import StreamAppsConfigurator from '../streaming/StreamAppsConfigurator';
import GuestVerificationPanel from '../streaming/GuestVerificationPanel';

/**
 * Go Live dialog — self-contained UI. All state lives in the parent
 * (LivePage) and is passed down as props. Purely presentational.
 */
const GoLiveDialog = ({
  // Dialog open state
  open, onOpenChange,
  t,
  // Mode toggles
  streamMode, setStreamMode,
  sourceMode, setSourceMode,
  // Form fields
  streamTitle, setStreamTitle,
  streamDesc, setStreamDesc,
  tokenGoal, setTokenGoal,
  recordVod, setRecordVod,
  // OBS mode
  obsKeyData, showObsKey, setShowObsKey, obsGoingLive, onStartObs,
  // Camera preview
  previewVideoRef,
  // Devices
  videoDevices, audioDevices,
  selectedVideoId, setSelectedVideoId,
  selectedAudioId, setSelectedAudioId,
  // 2257 guests
  myPerformerId,
  streamGuests, setStreamGuests,
  showGuestsPanel, setShowGuestsPanel,
  // Stream apps
  pendingApps, setPendingApps,
  showAppsPanel, setShowAppsPanel,
  // Geo
  blockedCountries, blockedStates,
  // Start
  starting, onStartLive,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button
          className="px-5 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors flex items-center gap-2 animate-pulse"
          data-testid="go-live-btn"
        >
          <Broadcast weight="fill" className="w-5 h-5" /> {t('live_go_live')}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg" aria-describedby="go-live-desc">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Start Streaming</DialogTitle>
          <p id="go-live-desc" className="text-sm text-slate-400">Set up your live stream</p>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {/* Browser / OBS mode toggle */}
          <div className="grid grid-cols-2 gap-2 p-1 bg-slate-800/60 rounded-xl" data-testid="stream-mode-toggle">
            <button
              type="button"
              onClick={() => setStreamMode('browser')}
              className={`py-2 text-sm font-bold rounded-lg transition-colors ${
                streamMode === 'browser' ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'
              }`}
              data-testid="mode-browser-btn"
            >Browser Webcam</button>
            <button
              type="button"
              onClick={() => setStreamMode('obs')}
              className={`py-2 text-sm font-bold rounded-lg transition-colors ${
                streamMode === 'obs' ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white'
              }`}
              data-testid="mode-obs-btn"
            >OBS / RTMPS</button>
          </div>

          {streamMode === 'obs' ? (
            /* ───────── OBS panel ───────── */
            <div className="space-y-3" data-testid="obs-panel">
              {/* 2-step process explainer — required until we wire the Cloudflare live-input webhook */}
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-100">
                <p className="font-bold text-amber-300 mb-1">Two-step start (required)</p>
                <ol className="list-decimal list-inside space-y-0.5 text-amber-100/90">
                  <li>Start Streaming <b>inside OBS</b> using the Server URL + Stream Key below.</li>
                  <li>Then click <b>Go Live</b> below to publish the stream on LustLink.</li>
                </ol>
                <p className="mt-1 text-[10px] text-amber-100/70">
                  Auto-detect from OBS ingest is on the roadmap — for now this 2-step is normal, not a bug.
                </p>
              </div>

              {!obsKeyData ? (
                <p className="text-sm text-slate-500 text-center py-6">Loading your OBS keys…</p>
              ) : (
                <>
                  <p className="text-xs text-slate-400">
                    In OBS go to <span className="text-white font-medium">Settings → Stream</span>, set
                    <span className="text-white font-medium"> Service: Custom</span>, paste these two values, and click Start Streaming inside OBS.
                    Then click <span className="text-blue-300 font-semibold">Go Live</span> below to publish on the Live page.
                  </p>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Server URL</label>
                    <div className="flex gap-2 mt-1">
                      <input readOnly value={obsKeyData.rtmp_ingest_url} className="flex-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-xs" />
                      <button type="button" onClick={() => { navigator.clipboard.writeText(obsKeyData.rtmp_ingest_url); toast.success('Server URL copied'); }}
                        className="px-3 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 text-xs"
                        data-testid="copy-rtmp-url-btn"
                      >Copy</button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Stream Key</label>
                    <div className="flex gap-2 mt-1">
                      <input readOnly type={showObsKey ? 'text' : 'password'} value={obsKeyData.stream_key}
                        className="flex-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-xs" />
                      <button type="button" onClick={() => setShowObsKey(s => !s)}
                        className="px-3 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 text-xs"
                      >{showObsKey ? 'Hide' : 'Show'}</button>
                      <button type="button" onClick={() => { navigator.clipboard.writeText(obsKeyData.stream_key); toast.success('Stream key copied'); }}
                        className="px-3 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 text-xs"
                        data-testid="copy-stream-key-btn"
                      >Copy</button>
                    </div>
                    <p className="text-[10px] text-slate-600 mt-1">Treat your stream key like a password. Anyone with it can broadcast as you.</p>
                  </div>
                </>
              )}

              <div>
                <Label>Stream Title</Label>
                <Input
                  value={streamTitle}
                  onChange={(e) => setStreamTitle(e.target.value)}
                  placeholder="What's your stream about?"
                  className="bg-slate-800 border-white/10 mt-1"
                  data-testid="obs-stream-title-input"
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Input
                  value={streamDesc}
                  onChange={(e) => setStreamDesc(e.target.value)}
                  className="bg-slate-800 border-white/10 mt-1"
                />
              </div>

              {/* Stream Apps (Chaturbate-style edge overlays) — same as Browser mode */}
              <div className="border border-white/10 rounded-xl overflow-hidden">
                <button type="button" onClick={() => setShowAppsPanel(!showAppsPanel)}
                  className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
                  data-testid="obs-stream-apps-toggle">
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-300">
                    <Broadcast className="w-4 h-4 text-blue-400" />
                    Stream Apps & Overlays
                    {pendingApps.length > 0 && (
                      <span className="px-1.5 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded-full">
                        {pendingApps.length}
                      </span>
                    )}
                  </span>
                  {showAppsPanel ? <CaretUp className="w-4 h-4 text-slate-400" /> : <CaretDown className="w-4 h-4 text-slate-400" />}
                </button>
                {showAppsPanel && (
                  <div className="p-3 border-t border-white/5 max-h-[400px] overflow-y-auto">
                    <StreamAppsConfigurator
                      initialApps={pendingApps}
                      onSave={setPendingApps}
                    />
                  </div>
                )}
              </div>

              {/* VOD-not-available notice for OBS (feature-gap disclosure) */}
              <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-3 text-xs text-blue-100" data-testid="obs-vod-notice">
                <p className="font-bold text-blue-300 mb-1">ℹ️ OBS VOD recording — coming soon</p>
                <p className="text-blue-100/90">
                  OBS streams are currently live-only. If you want to save this session as a replay, use <b>Browser Webcam</b> mode instead. Server-side recording for OBS is on our roadmap.
                </p>
              </div>

              <button
                onClick={onStartObs}
                disabled={obsGoingLive || !streamTitle.trim() || !obsKeyData}
                className="w-full py-3 bg-red-500 text-white rounded-full font-bold hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="start-obs-stream-btn"
              >
                <Broadcast weight="fill" className="w-5 h-5" />
                {obsGoingLive ? 'Going live…' : 'Go Live (OBS must already be connected)'}
              </button>
            </div>
          ) : (
          <>
          {/* Camera Preview */}
          <div className="rounded-xl overflow-hidden bg-black aspect-video relative">
            <video
              ref={previewVideoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover"
              data-testid="preview-video"
            />
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSourceMode('camera')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 ${
                  sourceMode === 'camera' ? 'bg-blue-500 text-white' : 'bg-slate-800/80 text-slate-400 hover:text-white'
                }`}
                data-testid="source-camera-btn"
              >
                <VideoCamera className="w-4 h-4" /> Camera
              </button>
              <button
                type="button"
                onClick={() => setSourceMode('screen')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 ${
                  sourceMode === 'screen' ? 'bg-blue-500 text-white' : 'bg-slate-800/80 text-slate-400 hover:text-white'
                }`}
                data-testid="source-screen-btn"
              >
                <Broadcast className="w-4 h-4" /> Screen
              </button>
            </div>
          </div>

          {/* Device pickers — populated after the preview grants permission */}
          {sourceMode === 'camera' && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="device-pickers">
              <div>
                <Label>Camera</Label>
                <select
                  value={selectedVideoId}
                  onChange={(e) => setSelectedVideoId(e.target.value)}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white mt-1 text-sm"
                  data-testid="camera-select"
                >
                  {videoDevices.length === 0 && <option value="">No cameras detected</option>}
                  {videoDevices.map(d => (
                    <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${d.deviceId.slice(0,6)}`}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Microphone</Label>
                <select
                  value={selectedAudioId}
                  onChange={(e) => setSelectedAudioId(e.target.value)}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white mt-1 text-sm"
                  data-testid="microphone-select"
                >
                  {audioDevices.length === 0 && <option value="">No microphones detected</option>}
                  {audioDevices.map(d => (
                    <option key={d.deviceId} value={d.deviceId}>{d.label || `Mic ${d.deviceId.slice(0,6)}`}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div>
            <Label>Stream Title</Label>
            <Input
              value={streamTitle}
              onChange={(e) => setStreamTitle(e.target.value)}
              placeholder="What's your stream about?"
              className="bg-slate-800 border-white/10 mt-1"
              data-testid="stream-title-input"
            />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea
              value={streamDesc}
              onChange={(e) => setStreamDesc(e.target.value)}
              placeholder="Tell viewers what to expect..."
              className="bg-slate-800 border-white/10 mt-1"
            />
          </div>
          <div>
            <Label>Token Goal (optional)</Label>
            <Input
              type="number"
              min="0"
              value={tokenGoal}
              onChange={(e) => setTokenGoal(parseInt(e.target.value) || 0)}
              className="bg-slate-800 border-white/10 mt-1"
            />
            <p className="text-xs text-slate-600 mt-1">Set 0 for no goal</p>
          </div>

          {/* Guests on stream (2257 compliance) */}
          <div className="border border-white/10 rounded-xl overflow-hidden">
            <button type="button" onClick={() => setShowGuestsPanel(!showGuestsPanel)}
              className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
              data-testid="guests-panel-toggle">
              <span className="flex items-center gap-2 text-sm font-medium text-slate-300">
                <User className="w-4 h-4 text-blue-400" />
                Guests on stream
                {streamGuests.length > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">
                    {streamGuests.length}
                  </span>
                )}
              </span>
              {showGuestsPanel ? <CaretUp className="w-4 h-4 text-slate-400" /> : <CaretDown className="w-4 h-4 text-slate-400" />}
            </button>
            {showGuestsPanel && (
              <div className="p-3 border-t border-white/5 max-h-[420px] overflow-y-auto">
                {myPerformerId && (
                  <div className="mb-3 flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2">
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider">Your Performer ID</p>
                      <p className="text-xs text-emerald-300 font-mono font-bold">{myPerformerId}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard?.writeText(myPerformerId); toast.success('Copied'); }}
                      className="text-xs text-blue-400 hover:text-blue-300"
                      data-testid="copy-my-performer-id"
                    >
                      Copy
                    </button>
                  </div>
                )}
                <p className="text-xs text-slate-500 mb-2">
                  Anyone else on camera must be verified. Add their Performer ID or verify them now.
                </p>
                <GuestVerificationPanel
                  guests={streamGuests}
                  onChange={setStreamGuests}
                />
              </div>
            )}
          </div>

          {/* Stream Apps (overlays / bots) */}
          <div className="border border-white/10 rounded-xl overflow-hidden">
            <button type="button" onClick={() => setShowAppsPanel(!showAppsPanel)}
              className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
              data-testid="stream-apps-toggle">
              <span className="flex items-center gap-2 text-sm font-medium text-slate-300">
                <Broadcast className="w-4 h-4 text-blue-400" />
                Stream Apps & Overlays
                {pendingApps.length > 0 && (
                  <span className="px-1.5 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded-full">
                    {pendingApps.length}
                  </span>
                )}
              </span>
              {showAppsPanel ? <CaretUp className="w-4 h-4 text-slate-400" /> : <CaretDown className="w-4 h-4 text-slate-400" />}
            </button>
            {showAppsPanel && (
              <div className="p-3 border-t border-white/5 max-h-[400px] overflow-y-auto">
                <StreamAppsConfigurator
                  initialApps={pendingApps}
                  onSave={setPendingApps}
                />
              </div>
            )}
          </div>

          {/* Record VOD toggle */}
          <label
            className="flex items-start gap-3 p-3 border border-white/10 rounded-xl cursor-pointer hover:bg-white/5 transition-colors"
            data-testid="record-vod-toggle"
          >
            <input
              type="checkbox"
              checked={recordVod}
              onChange={(e) => setRecordVod(e.target.checked)}
              className="mt-0.5 accent-blue-500 w-4 h-4"
            />
            <div>
              <p className="text-sm font-medium text-white">
                <Record weight="fill" className="inline w-4 h-4 mr-1 text-blue-400" />
                Record this stream (saves as VOD)
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                We&apos;ll record locally and let you save it as a replay when the stream ends. Off = no recording, no VOD.
              </p>
            </div>
          </label>

          {/* Geo-block summary — full editor lives in Settings now */}
          <div className="flex items-center justify-between p-3 border border-white/10 rounded-xl bg-slate-900/40">
            <span className="flex items-center gap-2 text-sm text-slate-300">
              <Globe className="w-4 h-4 text-blue-400" />
              <span>
                Geo-blocking:{' '}
                {(blockedCountries.length + blockedStates.length) > 0 ? (
                  <span className="text-blue-300 font-semibold">
                    {blockedCountries.length + blockedStates.length} region{(blockedCountries.length + blockedStates.length) !== 1 ? 's' : ''} blocked
                  </span>
                ) : (
                  <span className="text-slate-500">none</span>
                )}
              </span>
            </span>
            <a
              href="/settings#geo-blocking"
              className="text-xs text-blue-400 hover:text-blue-300 font-medium"
              data-testid="goto-geo-settings"
            >
              Edit in Settings →
            </a>
          </div>

          <button
            onClick={onStartLive}
            disabled={starting || !streamTitle.trim()}
            className="w-full py-3 bg-red-500 text-white rounded-full font-bold hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="start-stream-btn"
          >
            {starting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Broadcast weight="fill" className="w-5 h-5" />
                Go Live
              </>
            )}
          </button>
          </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GoLiveDialog;
